import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import snakeCase from 'lodash/snakeCase';
import includes from 'lodash/includes';
import { getStore, getIdentifier } from '~/mst/utils';
import { IWidget } from '~/mst/models/widget';
import { parseTags } from '~/utils/tags';
import { PERMISSIONS } from '~/mst/models/permissions/types';
import type { IDashboardModel } from './model';

export default (self: IDashboardModel) => {
  return {
    get owner() {
      return getStore(self).organizations.getById(self.organization_id);
    },
    get creator() {
      return getStore(self).users.getById(self.user_id);
    },
    get nodeIds() {
      return flow(
        map((widget: IWidget) => {
          const nodeIds = widget.node_id;
          if (includes(nodeIds, 'all')) {
            return widget.nodes?.toArray.map((node) => getIdentifier(node));
          }
          return nodeIds;
        }),
        flatten,
        uniq
      )(self.widgets.toArray);
    },
    get searchString() {
      return [self.name, self.description].join(' ').toLowerCase();
    },
    // Widget class names ("ChartWidget") become the same snake_case keys the widget
    // labels use ("chart_widget"), so filters.filter-tags.widget-type can translate them.
    get widgetTypeFilterTags() {
      return uniq(compact(self.widgets?.toArray.map((widget: IWidget) => (widget.type ? snakeCase(widget.type) : null))));
    },
    get tagsFilterTags() {
      return parseTags(self.description);
    },
    get filterTags() {
      return [...self.widgetTypeFilterTags, ...self.tagsFilterTags].map((tag) => tag.toLowerCase());
    },
    get canBeShared() {
      return !!self.user_id;
    },
    get isShared() {
      return !self.user_id;
    },
    get isOwn() {
      const { user } = getStore(self).auth;
      return Boolean(user && self.user_id === user.id);
    },
    // Mirrors the API: editing one's own takes Manage dashboards in the dashboard's organization,
    // a shared one Manage shared dashboards; a personal dashboard (no organization) is its owner's.
    get canEdit() {
      const { user } = getStore(self).auth;
      if (self.isOwn) {
        return !self.organization_id || Boolean(user?.hasPermission(PERMISSIONS.ManageDashboards, self.organization_id));
      }
      return self.isShared && Boolean(user?.hasPermission(PERMISSIONS.ManageSharedDashboards, self.organization_id));
    },
    // `is_starred` is saved on the dashboard itself, for everyone who sees it: only an own
    // dashboard is starred, and a star on a shared one (from before) doesn't count.
    get canStar() {
      return self.isOwn && self.canEdit;
    },
    get isStarred() {
      return self.is_starred && self.canStar;
    },
    // Organizations to share to: ones the user is a member of and manages shared dashboards in. An
    // own dashboard can go to its own organization; a shared one only moves to another.
    get shareOrganizations() {
      return getStore(self).organizations.valueLabelPairsCanManageSharedDashboards.filter(
        ({ model, value }) => model.is_member && (self.isOwn || value !== self.organization_id)
      );
    },
    // A clone is the user's own, in the source's organization: one they manage dashboards in. A
    // personal dashboard (no organization) takes managing dashboards somewhere, as in the API.
    get canClone() {
      const { user } = getStore(self).auth;
      return Boolean(user?.hasPermission(PERMISSIONS.ManageDashboards, self.organization_id || undefined));
    },
    // A manager takes a shared dashboard back as their own: it keeps no creator to return to.
    get canUnshare() {
      const { user } = getStore(self).auth;
      return self.isShared && Boolean(user?.hasPermission(PERMISSIONS.ManageSharedDashboards, self.organization_id || undefined));
    },
    // Only managers (Manage shared dashboards) share, and only their own dashboards.
    get canShare() {
      const { user } = getStore(self).auth;
      if (!self.isOwn) {
        return false;
      }
      return Boolean(user?.hasPermission(PERMISSIONS.ManageSharedDashboards, self.organization_id || undefined));
    }
  };
};
